import React, { useEffect, useState } from 'react';
import { Brain, Save } from 'lucide-react';
import { getSaasApiBaseUrl } from '../../utils/saasApiBaseUrl';
import { notifyOrgAIContributionUpdated } from '@saas-foundation/shared-frontend/analytics';

function formatApiError(payload) {
  const detail = payload?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || String(item)).join(' ');
  }
  return payload?.message || 'Request failed';
}

/**
 * Organization-level AI analytics visibility for all RCM service apps.
 * Source of truth: organizations.settings.rcm.show_ai_contribution.
 */
export default function OrganizationAIContributionSection({
  organization,
  onMessage,
  onSaved,
}) {
  const organizationId = organization?.id || organization?.organization_id;
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(organization?.settings?.rcm?.show_ai_contribution !== false);
  }, [organization]);

  const handleSave = async () => {
    if (!organizationId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(
        `${getSaasApiBaseUrl()}/organizations/${organizationId}/settings`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            rcm: { show_ai_contribution: enabled },
          }),
        }
      );
      if (!response.ok) {
        throw new Error(formatApiError(await response.json().catch(() => ({}))));
      }
      notifyOrgAIContributionUpdated();
      onMessage?.({
        type: 'success',
        text: 'AI contribution visibility saved for all RCM analytics apps.',
      });
      onSaved?.();
    } catch (error) {
      onMessage?.({
        type: 'error',
        text: error.message || 'Failed to save AI contribution setting.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-medium text-gray-900">AI contribution analytics</h2>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Controls whether AI contribution metrics appear across all RCM analytics apps.
          This setting applies to everyone in the organization.
        </p>
      </div>
      <div className="px-6 py-4 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
            disabled={saving || !organizationId}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-900">
              Show AI contribution metrics
            </span>
            <span className="block text-sm text-gray-500">
              Include AI-assisted outcomes, automation impact, and related metrics in business analytics.
            </span>
          </span>
        </label>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !organizationId}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save AI analytics setting'}
        </button>
      </div>
    </div>
  );
}
